<template>
  <div>
    <a-space>

      <a-dropdown v-if="!sizeHide" :trigger="['click']" :key="`dropdown-${currentLang}`">
        <a-button
          type="dashed"
          shape="circle"
          icon="column-height" />
        <a-menu slot="overlay" :selected-keys="[tableSize]" @click="changeSize" :key="`menu-${currentLang}`">
          <a-menu-item key="default">
            {{ defaultText }}
          </a-menu-item>
          <a-menu-item key="middle">
            {{ middleText }}
          </a-menu-item>
          <a-menu-item key="small">
            {{ smallText }}
          </a-menu-item>
        </a-menu>
      </a-dropdown>
      <a-popover v-if="!settingHide" placement="bottomRight" trigger="click" :key="`popover-${currentLang}`">
        <template slot="content">
          <a-tree
            class="draggable-tree"
            :show-icon="true"
            v-model="checkedKeys"
            checkable
            :replaceFields="replaceFields"
            draggable
            :tree-data="currentValue"
            :allow-drop="allowDrop"
            @drop="onDrop"
          />
        </template>
        <template slot="title">
          <a-checkbox :indeterminate="indeterminate" :checked="checkAll" @change="onCheckAllChange">
            {{ columnDisplayText }}
            <a-tooltip>
              <template slot="title">
                {{ columnSortTipText }}
              </template>
              <a-icon type="question-circle" />
            </a-tooltip>
          </a-checkbox>
        </template>
        <a-button
          type="dashed"
          shape="circle"
          icon="setting" />
      </a-popover>
      <a-button
        type="dashed"
        shape="circle"
        v-if="!refreshHide"
        :loading="refreshLoading"
        icon="reload"
        @click="refresh" />
    </a-space>
  </div>
</template>

<script>
import Ellipsis from '@/components/Ellipsis'
import i18nMixin from '@/store/i18n-mixin'

export default {
  name: 'NumberInfo',
  mixins: [i18nMixin],
  props: {
    tableSize: {
      type: String,
      default: 'default'
    },
    refreshLoading: {
      type: Boolean,
      default: false
    },
    value: {
      type: Array,
      required: true
    },
    refreshHide: {
      type: Boolean,
      default: false
    },
    settingHide: {
      type: Boolean,
      default: false
    },
    sizeHide: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Ellipsis
  },
  data () {
    return {
      allKey: [],
      indeterminate: true,
      checkAll: false,
      currentValue: [],
      checkedKeys: [],
      replaceFields: { children: 'children', title: 'currentTitle', key: 'dataIndex', slot: 'tsSlots' },
      isInternalUpdate: false // 标志，防止内部更新触发循环
    }
  },
  computed: {
    // 国际化文本，确保响应式更新
    defaultText () {
      return this.$t('table.default')
    },
    middleText () {
      return this.$t('table.middle')
    },
    smallText () {
      return this.$t('table.small')
    },
    columnDisplayText () {
      return this.$t('table.columnDisplay')
    },
    columnSortTipText () {
      return this.$t('table.columnSortTip')
    }
  },
  mounted () {
    // 如果 value watch 已经初始化过（immediate: true），这里就不需要重复初始化
    // 但如果 value 为空或未初始化，这里作为备用初始化
    if (this.allKey.length === 0 && this.value && this.value.length > 0) {
      this.currentValue = this.value
      this.currentValue.forEach(column => {
        this.allKey.push(column.dataIndex)
        column.currentTitle = <ellipsis length={16} tooltip>{column.title}</ellipsis>
        column.width2 = column.width
        // 如果 ellipsis 未定义，默认设置为 false，确保能正确控制显示/隐藏
        column.ellipsis2 = column.ellipsis !== undefined ? column.ellipsis : false
        // 判断列是否应该默认显示：宽度不为0且ellipsis不为true的列应该显示
        const isHidden = (column.width === 0 || column.width === '0' || column.width === '0%') && column.ellipsis === true
        if (!isHidden) {
          this.checkedKeys.push(column.dataIndex)
        }
        column.switcherIcon = <a-icon type="pic-left" />
      })
    }
  },
  watch: {
    value: {
      handler (newVal) {
        // 如果是内部更新触发的，直接返回，避免循环
        if (this.isInternalUpdate) {
          return
        }

        if (newVal && newVal.length > 0) {
          // 如果 allKey 为空，说明是首次初始化，需要设置 checkedKeys
          const isInitial = this.allKey.length === 0

          // 重新构建 allKey，确保与当前 columns 同步
          const newAllKey = []
          const newCheckedKeys = []

          // 保留现有列的扩展属性（width2, ellipsis2等）
          const existingColumnsMap = {}
          this.currentValue.forEach(col => {
            if (col.dataIndex) {
              existingColumnsMap[col.dataIndex] = {
                width2: col.width2,
                ellipsis2: col.ellipsis2,
                currentTitle: col.currentTitle,
                switcherIcon: col.switcherIcon
              }
            }
          })

          // 创建新数组，不直接修改 props，避免无限循环
          this.currentValue = newVal.map(column => {
            const existing = existingColumnsMap[column.dataIndex]
            // 创建新对象，保留原有属性并添加扩展属性
            const newColumn = { ...column }

            if (existing) {
              // 保留现有的扩展属性
              newColumn.width2 = existing.width2 !== undefined ? existing.width2 : column.width
              newColumn.ellipsis2 = existing.ellipsis2 !== undefined ? existing.ellipsis2 : (column.ellipsis !== undefined ? column.ellipsis : false)
              newColumn.currentTitle = existing.currentTitle || <ellipsis length={16} tooltip>{column.title}</ellipsis>
              newColumn.switcherIcon = existing.switcherIcon || <a-icon type="pic-left" />
            } else {
              // 新列，初始化扩展属性
              newColumn.width2 = column.width
              newColumn.ellipsis2 = column.ellipsis !== undefined ? column.ellipsis : false
              newColumn.currentTitle = <ellipsis length={16} tooltip>{column.title}</ellipsis>
              newColumn.switcherIcon = <a-icon type="pic-left" />
            }

            return newColumn
          })

          this.currentValue.forEach(column => {
            // 构建 allKey
            newAllKey.push(column.dataIndex)

            // 首次初始化时，根据列的当前状态设置 checkedKeys
            if (isInitial) {
              // 判断列是否应该默认显示：宽度不为0且ellipsis不为true的列应该显示
              const isHidden = (column.width === 0 || column.width === '0' || column.width === '0%') && column.ellipsis === true
              if (!isHidden) {
                newCheckedKeys.push(column.dataIndex)
              }
            } else {
              // 非首次初始化时，保持原有的选中状态（如果该列还存在）
              if (this.checkedKeys.indexOf(column.dataIndex) > -1) {
                newCheckedKeys.push(column.dataIndex)
              }
            }
          })

          // 更新 allKey 和 checkedKeys
          this.allKey = newAllKey
          if (isInitial) {
            this.checkedKeys = newCheckedKeys
          } else {
            // 非首次初始化时，只更新 checkedKeys 中仍然存在的列
            this.checkedKeys = this.checkedKeys.filter(key => newAllKey.indexOf(key) > -1)
          }
        }
      },
      immediate: true,
      deep: true
    },
    currentLang () {
      // 语言切换时，强制更新组件
      this.$forceUpdate()
    },
    checkedKeys (cur, last) {
      // 遍历所有列，根据当前 checkedKeys 状态统一设置显示/隐藏
      this.currentValue.forEach(column => {
        const isChecked = cur.indexOf(column.dataIndex) > -1
        if (isChecked) {
          // 列被选中：恢复显示，恢复原始宽度和 ellipsis 值
          // 如果 width2 是 undefined 或 null，则删除 width 属性（让表格自动计算宽度）
          if (column.width2 !== undefined && column.width2 !== null && column.width2 !== '') {
            this.$set(column, 'width', column.width2)
          } else {
            // 如果原来没有 width，删除它以确保表格自动计算
            if (column.hasOwnProperty('width')) {
              this.$delete(column, 'width')
            }
          }
          // 如果 ellipsis2 是 undefined，恢复时设置为 false
          this.$set(column, 'ellipsis', column.ellipsis2 !== undefined ? column.ellipsis2 : false)
        } else {
          // 列未选中：隐藏列，设置宽度为 0，ellipsis 为 true
          this.$set(column, 'width', 0)
          this.$set(column, 'ellipsis', true)
        }
      })

      this.indeterminate = !!cur.length && cur.length < this.allKey.length
      this.checkAll = cur.length === this.allKey.length

      // 触发更新，使用新数组确保响应式更新
      this.isInternalUpdate = true
      this.$emit('input', this.currentValue.slice())
      this.$nextTick(() => {
        this.isInternalUpdate = false
      })
    }
  },
  methods: {
    changeSize (e) {
      this.$emit('update:tableSize', e.key)
    },
    onCheckAllChange (e) {
      Object.assign(this, {
        checkedKeys: e.target.checked ? this.allKey : [],
        indeterminate: false,
        checkAll: e.target.checked
      })
    },
    refresh () {
      this.$emit('refresh')
    },
    allowDrop ({ dropNode, dropPosition }) {
      // 允许在所有位置拖放（同级节点之间）
      // 返回 true 表示允许拖放
      if (!dropNode) {
        return false
      }
      // 允许在同一层级内拖放
      return true
    },
    onDrop (info) {
      const dropKey = info.node.eventKey
      const dragKey = info.dragNode.eventKey
      const dropPos = info.node.pos.split('-')
      const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1])
      const loop = (data, key, callback) => {
        data.forEach((item, index, arr) => {
          if (item.dataIndex === key) {
            return callback(item, index, arr)
          }
          if (item.children) {
            return loop(item.children, key, callback)
          }
        })
      }
      // 创建新数组，但保留对象的引用（保留 JSX 元素和 Vue 响应式属性）
      const data = this.currentValue.map(col => ({ ...col }))

      // Find dragObject
      let dragObj
      loop(data, dragKey, (item, index, arr) => {
        arr.splice(index, 1)
        dragObj = item
      })
      let ar
      let i
      loop(data, dropKey, (item, index, arr) => {
        ar = arr
        i = index
      })
      if (dropPosition === -1) {
        ar.splice(i, 0, dragObj)
      } else {
        ar.splice(i + 1, 0, dragObj)
      }
      this.currentValue = data
      // 使用新数组触发更新
      this.isInternalUpdate = true
      this.$emit('input', [...data])
      this.$nextTick(() => {
        this.isInternalUpdate = false
      })
    }
  }
}
</script>

<style scoped>
.draggable-tree {
  max-height: 400px;
  overflow-y: auto;
}

.draggable-tree >>> .ant-tree-node-content-wrapper {
  cursor: move;
  user-select: none;
}

.draggable-tree >>> .ant-tree-node-content-wrapper:hover {
  background-color: #f5f5f5;
}

.draggable-tree >>> .ant-tree-draggable-icon {
  cursor: move;
}

.draggable-tree >>> .ant-tree-treenode-draggable {
  cursor: move;
}
</style>
